/**
 * A campaign's own questions to a donor, and what makes a set of answers to
 * them acceptable.
 *
 * Stated once, here, because the donate page and the server both ask it: the
 * page to say which question still needs an answer, the server to refuse a
 * gift that skipped a required one whatever the page did.
 */

export type CampaignQuestionKind = 'one' | 'several'

export const CAMPAIGN_QUESTION_PROMPT_MAX = 200
export const CAMPAIGN_QUESTION_OPTION_MAX = 120
/** A question is read before paying, so its list stays one a donor reads. */
export const CAMPAIGN_QUESTION_OPTIONS_MAX = 30
export const CAMPAIGN_QUESTIONS_MAX = 10

/** A question as the page asks it: its live options only. */
export type AskedQuestion = {
  id: string
  prompt: string
  kind: CampaignQuestionKind
  required: boolean
  options: Array<{ id: string; label: string }>
}

/** What a donor picked for one question; an empty list is no answer. */
export type GivenAnswer = { questionId: string; optionIds: string[] }

/**
 * Why these answers cannot go with a gift, or null when they can.
 *
 * Refused: a required question left empty, more than one pick on a pick-one
 * question, a pick that is not one of the question's live options, and an
 * answer to a question the campaign does not ask. A question left out of the
 * list entirely reads as unanswered, never as an error.
 */
export function campaignAnswersProblem(
  questions: AskedQuestion[],
  answers: GivenAnswer[],
  /**
   * Whether a required question must be answered. It binds a donor; it never
   * binds the office, which records money already in hand and often does not
   * know the answer (product owner, 2026-10-05).
   */
  options: { requiredBinds?: boolean } = {},
): { questionId: string | null; message: string } | null {
  const byId = new Map(questions.map((q) => [q.id, q]))
  const answered = new Set<string>()
  for (const a of answers) {
    /* One answer per question: a second would be a pick nobody reads, since
       the first is the one kept. */
    if (answered.has(a.questionId)) {
      return {
        questionId: a.questionId,
        message: 'A question was answered twice.',
      }
    }
    answered.add(a.questionId)
    const q = byId.get(a.questionId)
    if (!q) {
      return {
        questionId: a.questionId,
        message:
          'One of the questions is no longer asked. Reload the page and answer again.',
      }
    }
    if (new Set(a.optionIds).size !== a.optionIds.length) {
      return { questionId: q.id, message: 'An answer was picked twice.' }
    }
    if (q.kind === 'one' && a.optionIds.length > 1) {
      return {
        questionId: q.id,
        message: `Choose one answer to "${q.prompt}".`,
      }
    }
    const live = new Set(q.options.map((o) => o.id))
    if (a.optionIds.some((id) => !live.has(id))) {
      return {
        questionId: q.id,
        message: `One of the answers to "${q.prompt}" is no longer offered. Choose again.`,
      }
    }
  }
  if (options.requiredBinds === false) return null
  for (const q of questions) {
    if (!q.required) continue
    const given = answers.find((a) => a.questionId === q.id)
    if (!given || given.optionIds.length === 0) {
      return {
        questionId: q.id,
        message:
          q.kind === 'one'
            ? `Choose an answer to "${q.prompt}".`
            : `Choose at least one answer to "${q.prompt}".`,
      }
    }
  }
  return null
}
